import { useState, useRef } from 'react';
import { useApp } from '@/contexts/AppContext';
import { backupAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Upload, Key, Check, AlertTriangle, Shield, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { decryptBackup } from '@/lib/crypto';

export default function BackupRestoreDialog() {
  const { fetchNotes, fetchNotebooks, fetchTags } = useApp();

  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreStep, setRestoreStep] = useState('upload');
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreBundle, setRestoreBundle] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreRecoveryKey, setRestoreRecoveryKey] = useState('');
  const [restoreIsEncrypted, setRestoreIsEncrypted] = useState(false);
  const restoreInputRef = useRef(null);

  const loadPreview = async (bundle, mode = restoreMode) => {
    setRestoreLoading(true);
    try {
      const res = await backupAPI.restorePreview({ manifest: bundle.manifest, data: bundle.data, mode });
      setRestorePreview(res.data);
      setRestoreStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to analyze backup');
    }
    setRestoreLoading(false);
  };

  const handleRestoreFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoreFile(file);
    setRestoreStep('upload');
    setRestorePreview(null);
    setRestoreResult(null);
    setRestoreRecoveryKey('');

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      if (parsed.encrypted && parsed.iv) {
        setRestoreIsEncrypted(true);
        setRestoreBundle(parsed);
      } else if (parsed.manifest && parsed.data) {
        setRestoreIsEncrypted(false);
        setRestoreBundle(parsed);
        await loadPreview(parsed);
      } else {
        toast.error('Unrecognized backup format');
        setRestoreFile(null);
      }
    } catch {
      toast.error('Failed to parse backup file');
      setRestoreFile(null);
    }
    e.target.value = '';
  };

  const handleDecryptAndPreview = async () => {
    if (!restoreRecoveryKey.trim()) { toast.error('Enter recovery key'); return; }
    setRestoreLoading(true);
    try {
      const normalizedKey = restoreRecoveryKey.trim().toLowerCase().replace(/\s+/g, ' ');
      const decrypted = await decryptBackup(restoreBundle, normalizedKey);
      setRestoreIsEncrypted(false);
      setRestoreBundle(decrypted);
      await loadPreview(decrypted);
    } catch (err) {
      if (err.name === 'OperationError') toast.error('Invalid recovery key');
      else toast.error('Decryption failed');
    }
    setRestoreLoading(false);
  };

  const handleRestore = async () => {
    setRestoreLoading(true);
    try {
      const res = await backupAPI.restore({ manifest: restoreBundle.manifest, data: restoreBundle.data, mode: restoreMode });
      setRestoreResult(res.data);
      setRestoreStep('done');
      await fetchNotes();
      await fetchNotebooks();
      await fetchTags();
      toast.success('Backup restored successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
    setRestoreLoading(false);
  };

  const resetRestore = () => {
    setShowRestoreDialog(false);
    setRestoreFile(null);
    setRestoreBundle(null);
    setRestorePreview(null);
    setRestoreResult(null);
    setRestoreStep('upload');
    setRestoreRecoveryKey('');
    setRestoreIsEncrypted(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Upload className="w-4 h-4 text-accent-cyan" />
        <h3 className="text-sm font-semibold">Restore Backup</h3>
      </div>

      <Button onClick={() => setShowRestoreDialog(true)} variant="outline" className="w-full" size="sm" data-testid="restore-backup-btn">
        <Upload className="w-3.5 h-3.5 mr-1.5" /> Restore from Backup
      </Button>

      <AlertDialog open={showRestoreDialog} onOpenChange={(open) => { if (!open) resetRestore(); }}>
        <AlertDialogContent className="sm:max-w-lg" data-testid="restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {restoreStep === 'upload' && 'Restore Backup'}
              {restoreStep === 'preview' && 'Review Before Restoring'}
              {restoreStep === 'done' && 'Restore Complete'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {restoreStep === 'upload' && 'Select a VaultNotes backup file (.json)'}
              {restoreStep === 'preview' && 'Review what will change before proceeding'}
              {restoreStep === 'done' && 'Your data has been restored'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Upload step */}
          {restoreStep === 'upload' && (
            <div className="space-y-4 py-2">
              <input ref={restoreInputRef} type="file" accept=".json" onChange={handleRestoreFileSelect} className="hidden" />
              {!restoreFile ? (
                <Button variant="outline" className="w-full h-20 flex-col gap-1.5 border-dashed" onClick={() => restoreInputRef.current?.click()} data-testid="restore-select-file">
                  <Upload className="w-5 h-5" />
                  <span className="text-xs">Select Backup File</span>
                </Button>
              ) : (
                <div className="p-3 bg-secondary/30 rounded-lg border border-border/40">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-foreground flex-1 truncate">{restoreFile.name}</span>
                    {restoreIsEncrypted && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">Encrypted</span>}
                  </div>
                </div>
              )}

              {restoreIsEncrypted && restoreFile && (
                <div className="space-y-2">
                  <Label className="text-xs">Recovery Key (24 words)</Label>
                  <textarea
                    value={restoreRecoveryKey}
                    onChange={(e) => setRestoreRecoveryKey(e.target.value)}
                    placeholder="Enter your 24-word recovery phrase..."
                    className="w-full h-20 px-3 py-2 text-xs bg-input border border-border rounded resize-none outline-none focus:ring-1 focus:ring-ring"
                    data-testid="restore-recovery-key-input"
                  />
                  <Button onClick={handleDecryptAndPreview} disabled={restoreLoading} className="w-full" size="sm" data-testid="restore-decrypt-btn">
                    {restoreLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Key className="w-3.5 h-3.5 mr-1.5" />}
                    Decrypt & Preview
                  </Button>
                </div>
              )}

              {restoreLoading && !restoreIsEncrypted && (
                <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              )}
            </div>
          )}

          {/* Preview step */}
          {restoreStep === 'preview' && restorePreview && (
            <div className="space-y-4 py-2">
              <div className={`p-2 rounded flex items-center gap-2 text-xs ${
                restorePreview.checksum_valid
                  ? 'bg-green-500/10 border border-green-500/20 text-green-500'
                  : 'bg-red-500/10 border border-red-500/20 text-red-500'
              }`} data-testid="restore-checksum-status">
                {restorePreview.checksum_valid ? <Shield className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                {restorePreview.checksum_valid ? 'Integrity verified' : 'Checksum mismatch — data may be modified'}
              </div>

              {restorePreview.manifest && (
                <div className="text-xs text-muted-foreground">
                  <p>Created: {new Date(restorePreview.manifest.created_at).toLocaleString()}</p>
                  {restorePreview.manifest.user_email && <p>By: {restorePreview.manifest.user_email}</p>}
                </div>
              )}

              <div className="border border-border/40 rounded-lg divide-y divide-border/30 text-xs">
                {['notes', 'notebooks', 'tags', 'templates'].map((key) => {
                  const p = restorePreview.preview[key];
                  return (
                    <div key={key} className="flex items-center justify-between px-3 py-2" data-testid={`restore-preview-${key}`}>
                      <span className="text-muted-foreground capitalize">{key}</span>
                      <div className="flex items-center gap-2">
                        {p.new > 0 && <span className="text-green-500">+{p.new} new</span>}
                        {p.overwrite > 0 && <span className="text-amber-500">{p.overwrite} overwrite</span>}
                        <span className="text-foreground font-medium">{p.total} total</span>
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-muted-foreground">Reminders</span>
                  <span className="font-medium">{restorePreview.preview.reminders.total}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-muted-foreground">Attachments (metadata)</span>
                  <span className="font-medium">{restorePreview.preview.attachments.total}</span>
                </div>
              </div>

              {restorePreview.warnings?.length > 0 && (
                <div className="space-y-1.5">
                  {restorePreview.warnings.map((w) => (
                    <div key={w} className="p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-500 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-medium">Restore Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setRestoreMode('merge'); loadPreview(restoreBundle, 'merge'); }}
                    className={`p-3 rounded-lg border text-left transition-colors ${restoreMode === 'merge' ? 'border-accent-amber/50 bg-accent-amber/5' : 'border-border/40 hover:border-border'}`}
                    data-testid="restore-mode-merge"
                  >
                    <p className="text-xs font-medium text-foreground">Merge</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Add new data, overwrite conflicts</p>
                  </button>
                  <button
                    onClick={() => { setRestoreMode('replace'); loadPreview(restoreBundle, 'replace'); }}
                    className={`p-3 rounded-lg border text-left transition-colors ${restoreMode === 'replace' ? 'border-red-500/50 bg-red-500/5' : 'border-border/40 hover:border-border'}`}
                    data-testid="restore-mode-replace"
                  >
                    <p className="text-xs font-medium text-foreground">Replace</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Delete all existing, import fresh</p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Done step */}
          {restoreStep === 'done' && restoreResult && (
            <div className="space-y-4 py-2" data-testid="restore-result">
              <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/5 text-center">
                <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Restore Complete ({restoreResult.mode} mode)</p>
              </div>
              <div className="text-xs space-y-1.5">
                {Object.entries(restoreResult.stats).map(([key, val]) => (
                  <div key={key} className="flex justify-between py-1 border-b border-border/30">
                    <span className="text-muted-foreground capitalize">{key}</span>
                    <span className="font-medium">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            {restoreStep === 'preview' && (
              <>
                <AlertDialogCancel onClick={resetRestore}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleRestore} disabled={restoreLoading || (!restorePreview?.checksum_valid && restorePreview !== null)} data-testid="restore-confirm-btn">
                  {restoreLoading ? 'Restoring...' : restoreMode === 'replace' ? 'Replace All Data' : 'Merge & Restore'}
                </AlertDialogAction>
              </>
            )}
            {restoreStep === 'done' && (
              <AlertDialogAction onClick={resetRestore} data-testid="restore-done-btn">Done</AlertDialogAction>
            )}
            {restoreStep === 'upload' && (
              <AlertDialogCancel onClick={resetRestore}>Cancel</AlertDialogCancel>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
